import { ApolloLink, HttpLink, ApolloClient, from, InMemoryCache, useLazyQuery as useLazyQuery$1, useMutation as useMutation$1, useQuery as useQuery$1 } from '@apollo/client';
export { ApolloConsumer, ApolloError, ApolloProvider, InMemoryCache, NetworkStatus, defaultDataIdFromObject, gql, makeVar, useApolloClient, useReactiveVar, useSubscription } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as React from 'react';
import { ApolloLink as ApolloLink$1 } from '@apollo/client/link/core';
import { MockLink, MockedProvider } from '@apollo/client/testing';
export { MockedProvider } from '@apollo/client/testing';
export * from 'graphql';

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
// TODO: fix/improve token caching
// This is a hardcoded map of the queries and mutations that require authentication, when this
// map isn't in place, every single graphQL request will also send a request to
// check the session of auth0, this is a naive way to skip over all the graphQL
// queries which we know don't require authentication
const authOperations = new Set([
    'CancelSubscription',
    'ChangeSubscription',
    'CheckCustomerConsents',
    'CreateEyeExam',
    'CreateStoredCardPayment',
    'CreateStoredSepaPaymentMethod',
    'findSubscription',
    'getSuspendSubscription',
    'getChangeSubscriptionPreview',
    'getCurrentSubscriptionDetails',
    'getPrescriptionDownloads',
    'getMyAppointments',
    'getMySubscriptions',
    'getSubscriptionDeliveries',
    'getSubscriptionPayments',
    'getSubscriptionPrescription',
    'getSubscriptionPrescriptionProducts',
    'getSubscriptionNotifications',
    'getSubscriptionProductRateplans',
    'getSuspensionResumeDates',
    'previewChangeSubscription',
    'ResumeSubscription',
    'RetrySubscriptionPayment',
    'SaveNewConsent',
    'SuspendSubscription',
    'UpdateBillingAddress',
    'UpdateDeliveryAddress',
    'UpdateSubscriptionPrescription',
    'storedPaymentMethods',
    'ChangePaymentMethod',
    'complete3DSChallengeFlowStoredPayment', 
]);
const authCallback = (options)=>(operation, previousRequest)=>new Promise(function() {
            var _ref = _asyncToGenerator$1(function*(resolve, reject) {
                // Only continue if the operationName is one that we know requires authentication
                const requiresAuth = operation.operationName && authOperations.has(operation.operationName);
                if (!requiresAuth || !options.getToken) return resolve(previousRequest);
                try {
                    const token = yield options.getToken();
                    resolve({
                        headers: _objectSpread$2({}, previousRequest.headers, {
                            authorization: `Bearer ${token}`
                        })
                    });
                } catch (e) {
                    reject(e);
                }
            });
            return function(resolve, reject) {
                return _ref.apply(this, arguments);
            };
        }())
;
const authLink = (options)=>setContext(authCallback(options))
;
const httpLink = (options)=>new HttpLink(_objectSpread$2({
        credentials: 'same-origin',
        fetch
    }, options))
;
const errorLink = onError(({ graphQLErrors , networkError  })=>{
    if (graphQLErrors) {
        graphQLErrors.forEach((error)=>console.error(`[GraphQL error]: ${error.message}`)
        );
    }
    if (networkError) {
        console.error('[Network error]:', networkError);
    }
});
const forwardHeaders = (operation, forward)=>{
    const context = operation.getContext();
    const forwardHeaders1 = context.forwardHeaders;
    operation.setContext(({ headers ={}  })=>({
            headers: _objectSpread$2({}, headers, forwardHeaders1 && forwardHeaders1)
        })
    );
    return forward(operation);
};
const forwardHeadersLink = new ApolloLink(forwardHeaders);

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
let apolloClient;
const initApolloClient = (uri, options)=>{
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections
    if (typeof window === 'undefined') {
        return createApolloClient(uri, options);
    }
    if (!apolloClient) {
        apolloClient = createApolloClient(uri, options);
    }
    return apolloClient;
};
const createApolloClient = (uri, options = {})=>{
    if (!uri) {
        console.error('No graphQL endpoint specified.');
        throw new Error('No graphQL endpoint specified.');
    }
    const cacheConfig = options.cacheConfig || {};
    const initialState = options.initialState || {};
    const queryOptions = options.queryOptions || {};
    const headers = {};
    const persistedQueriesLink = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true
    });
    return new ApolloClient({
        ssrMode: typeof window !== 'undefined',
        link: from([
            errorLink,
            forwardHeadersLink,
            authLink(options),
            ...queryOptions.isPersistentQueryDisabled ? [] : [
                persistedQueriesLink
            ],
            httpLink({
                headers,
                uri
            }), 
        ]),
        cache: new InMemoryCache(_objectSpread$1({
            typePolicies: {
                LegacyProduct: {
                    keyFields: [
                        'sku'
                    ]
                },
                Variant: {
                    keyFields: [
                        'gan'
                    ]
                },
                ContactLensVariant: {
                    keyFields: [
                        'gan'
                    ]
                },
                ContactLensProduct: {
                    keyFields: [
                        'productMasterKey'
                    ]
                },
                SolutionVariant: {
                    keyFields: [
                        'gan'
                    ],
                    fields: {
                        product: {
                            // Custom merge function tells apollo its safe to merge SolutionProducts which are direct descendants of SolutionVariants, this effectively uses the parent SolutionVariant 'gan' as the unique identifier for SolutionProduct
                            // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
                            merge (existing, incoming) {
                                return _objectSpread$1({}, existing, incoming);
                            }
                        }
                    }
                }
            }
        }, cacheConfig)).restore(initialState)
    });
};

var AccountActivationPayload = [
    "AccountActivatedSuccessfully",
    "CustomerAccountActivationMaxRetriesError"
];
var Address = [
    "BillingAddress",
    "DeliveryAddress"
];
var CompletedPaymentResultData = [
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var Domain = [
    "ContactLensProduct",
    "FrameProduct",
    "SolutionProduct",
    "SunglassesProduct"
];
var LineItem = [
    "LineItemBundle",
    "LineItemContactLenses",
    "LineItemRegular"
];
var Location = [
    "PickupPoint",
    "Store"
];
var MutationPayload = [
    "CompletePaymentPayload",
    "StoredPaymentMethodMutationPayload"
];
var MutationResponse = [
    "CartMutationResponse",
    "ShareCartMutationResponse",
    "TicketMutationResponse"
];
var PaymentTransactionMethod = [];
var Prescription = [
    "PrescriptionDownload"
];
var Product = [
    "ContactLensProduct",
    "FrameProduct",
    "SolutionProduct",
    "SunglassesProduct"
];
var RatePlan = [
    "ContactLensRatePlan",
    "SolutionRatePlan"
];
var RecommendationBannerAction = [
    "RecommendationBannerApplyDiscountCodeAction",
    "RecommendationBannerRedirectToLinkAction"
];
var RecommendationProductAction = [
    "RecommendationProductAddToCartAction",
    "RecommendationProductRedirectAction"
];
var StoreGroupInterface = [
    "SingleStoreGroup",
    "StoreGroup"
];
var StoredPaymentMethod = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var StoredPaymentMethodResult = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var StoredPaymentResult = [
    "Payment3DSChallengeData",
    "PaymentRedirectData",
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var StoredPaymentResultData = [
    "Payment3DSChallengeData",
    "PaymentRedirectData",
    "StoredPaymentErrorData",
    "StoredPaymentRefusedData",
    "StoredPaymentSuccessData"
];
var SubscriptionProductVariant = [
    "ContactLensVariant",
    "SolutionVariant"
];
var SubscriptionStoredPaymentMethod = [
    "StoredCardPaymentMethod",
    "StoredSEPAPaymentMethod"
];
var TypeDetails = [
    "BundleNonPrescriptionBundleTypeDetails",
    "BundlePrescriptionGlassesTypeDetails",
    "ContactLensBundleBeneluxTypeDetails",
    "ContactLensSubscriptionTypeDetails",
    "ContactLensesTypeDetails",
    "RegularTypeDetails"
];
var Variant = [
    "ContactLensVariant",
    "FrameVariant",
    "SolutionVariant",
    "SunglassesVariant"
];
var possibleTypes = {
    AccountActivationPayload: AccountActivationPayload,
    Address: Address,
    CompletedPaymentResultData: CompletedPaymentResultData,
    Domain: Domain,
    LineItem: LineItem,
    Location: Location,
    MutationPayload: MutationPayload,
    MutationResponse: MutationResponse,
    PaymentTransactionMethod: PaymentTransactionMethod,
    Prescription: Prescription,
    Product: Product,
    RatePlan: RatePlan,
    RecommendationBannerAction: RecommendationBannerAction,
    RecommendationProductAction: RecommendationProductAction,
    StoreGroupInterface: StoreGroupInterface,
    StoredPaymentMethod: StoredPaymentMethod,
    StoredPaymentMethodResult: StoredPaymentMethodResult,
    StoredPaymentResult: StoredPaymentResult,
    StoredPaymentResultData: StoredPaymentResultData,
    SubscriptionProductVariant: SubscriptionProductVariant,
    SubscriptionStoredPaymentMethod: SubscriptionStoredPaymentMethod,
    TypeDetails: TypeDetails,
    Variant: Variant
};

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DebugMockedProvider = (_param)=>{
    var { children , mocks , addPossibleTypes  } = _param, rest = _objectWithoutProperties(_param, [
        "children",
        "mocks",
        "addPossibleTypes"
    ]);
    const mockLink = new MockLink(mocks || []);
    const link = ApolloLink$1.from([
        errorLink,
        mockLink
    ]);
    return(/*#__PURE__*/ React.createElement(MockedProvider, _extends({
        cache: new InMemoryCache(addPossibleTypes ? {
            possibleTypes
        } : {}),
        addTypename: false,
        link: link
    }, rest), children));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
const wait = function() {
    var _ref = _asyncToGenerator(function*(ms) {
        return new Promise((resolve)=>setTimeout(resolve, ms)
        );
    });
    return function wait(ms) {
        return _ref.apply(this, arguments);
    };
}();
// Find the given errorCode, when facing an internal server error, the errorcode can be found within the original error
const getGraphQLErrorCode = (error1, errorCode)=>{
    return error1.graphQLErrors.find((error)=>{
        var ref, ref1;
        return ((ref = error.extensions) === null || ref === void 0 ? void 0 : ref.code) === errorCode || ((ref1 = error.extensions) === null || ref1 === void 0 ? void 0 : ref1.exception.originalError.extensions.code) === errorCode;
    });
};

const getOperationInfo = (query)=>{
    const operationNode = query.definitions[0];
    let name = 'anonymous';
    // Fallback name when the operation has been malformed but still send to the BE
    let type = operationNode.kind;
    if (operationNode.kind === 'OperationDefinition') {
        type = operationNode.operation;
        if (operationNode.name) {
            name = operationNode.name.value;
        }
    }
    return [
        type,
        name
    ];
};
const handleError = (query, error)=>{
    const { graphQLErrors  } = error;
    const { Sentry  } = window;
    const [type, name] = getOperationInfo(query);
    // logging package does not have scope or capture exceptions as of yet.
    if (Sentry) {
        if (graphQLErrors.length) {
            Sentry.withScope((scope)=>{
                scope.setTag('type', 'graphql');
                Sentry.captureException(error);
            });
        } else {
            Sentry.captureException(error);
        }
    }
    console.error(`[${type}] - ${name} - error`, {
        error
    });
};

const useLazyQuery = (query, options)=>{
    const [executable, result] = useLazyQuery$1(query, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(query, error);
        }
    }, [
        error,
        query
    ]);
    return [
        executable,
        result
    ];
};

const useMutation = (mutation, options)=>{
    const [executable, result] = useMutation$1(mutation, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(mutation, error);
        }
    }, [
        error,
        mutation
    ]);
    return [
        executable,
        result
    ];
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
/**
 * This hooks allows for paginated GraphQL queries. It will return the data
 * in a paged format, meaning you get an array containing arrays of
 * length `pageSize`.
 *
 * How to use the pages is up to you.
 *
 * The hook is type-safe, types for both the data returned from the query, as
 * well as the actual paginatable data is provided through generics.
 * @param query GraphQL query with pagination support
 * @param pageSize Amount of items per page
 * @param pagedDataSelector Selector to get to paginatable data
 * @param options Optional, query variables
 * @returns [{total, pages}, fetchMore, loading, error]
 * @example
 * const [{ total, pages }, fetchNext, loading, error] = usePaginatedQuery<
 *   getMyOrderHistory,
 *   getMyOrderHistory_me_orders_results
 * >(GET_MY_ORDER_HISTORY, 5, (result) => result.me?.orders)
 */ function usePaginatedQuery(query, pageSize, pagedDataSelector, options) {
    const [pageCursor, setPageCursor] = React.useState({
        offset: 0,
        limit: pageSize
    });
    const [total, setTotal] = React.useState(0);
    const [pages1, setPages] = React.useState(null);
    const [executable, { data , loading , error  }] = useLazyQuery$1(query, {
        variables: _objectSpread({}, options, pageCursor)
    });
    const fetchNext = React.useCallback(()=>{
        const newCursor = _objectSpread({}, pageCursor, {
            offset: pageCursor.offset + pageSize
        });
        setPageCursor(newCursor);
        executable({
            variables: _objectSpread({}, options, newCursor)
        });
    }, [
        executable,
        options,
        pageCursor,
        pageSize
    ]);
    React.useEffect(()=>{
        if (error) {
            handleError(query, error);
        }
    }, [
        error,
        query
    ]);
    /**
   * Append data coming in from a fetch to the existing
   * array of pages
   */ React.useEffect(()=>{
        if (!data) {
            return;
        }
        const paginatedData = pagedDataSelector(data) //extract paginated data
        ;
        if ((paginatedData === null || paginatedData === void 0 ? void 0 : paginatedData.results) && !loading && !error) {
            setPages((pages)=>pages === null ? [
                    paginatedData.results
                ] : pages.concat([
                    paginatedData.results
                ])
            );
            setTotal(paginatedData.total);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data
    ]);
    /**
   * Initial fetch
   */ React.useEffect(()=>{
        if (pages1 === null && !loading && !error) {
            executable();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return React.useMemo(()=>[
            {
                fetchNext
            },
            {
                data: {
                    total,
                    pages: pages1 !== null && pages1 !== void 0 ? pages1 : [
                        []
                    ]
                },
                loading,
                error
            }, 
        ]
    , [
        fetchNext,
        total,
        pages1,
        loading,
        error
    ]);
}

const useQuery = (query, options)=>{
    const result = useQuery$1(query, options);
    const { error  } = result;
    React.useEffect(()=>{
        if (error) {
            handleError(query, error);
        }
    }, [
        error,
        query
    ]);
    return result;
};

export { DebugMockedProvider, getGraphQLErrorCode, initApolloClient, useLazyQuery, useMutation, usePaginatedQuery, useQuery, wait };
//# sourceMappingURL=index.js.map
